import {ButtonGroup, closeCurrentDialog} from 'platform/components';
import {Box, VStack} from 'platform/foundation';

import {useState} from 'react';

import {isNotNilOrEmpty} from 'ramda-adjunct';

import i18n from '@dms/i18n';

import type {RowData} from 'features/eag-grid';

import {DmsDataGrid} from '../DmsDataGrid/DmsDataGrid';

// BE multi-select employee picker datagrid (T20-128011 / MR !13909); row id = employee id.
const PARTICIPANTS_PICKER_GRID_CODE = 'record-audit-pair-employee-multiple';

interface AddParticipantDialogProps {
  existingEmployeeIds: string[];
  onConfirm: (participants: Array<{employeeId: string}>) => void;
}

/**
 * Single-step participant picker backed by the BE `record-audit-pair-employee-multiple` datagrid
 * (grid row id = employee UUID). Multi-selects employees and emits one participant per pick; the
 * caller appends them all. Roles are not assigned here — each participant's professions are shown
 * read-only on the team panel from their organisational data.
 */
export function AddParticipantDialog(props: AddParticipantDialogProps) {
  const [pickedEmployeeIds, setPickedEmployeeIds] = useState<string[]>([]);

  // Already-assigned employees are dropped from the picks so they can't be added twice.
  const handleSelectionChange = (rows: RowData[]) => {
    setPickedEmployeeIds(
      rows.map((row) => row.id).filter((id) => !props.existingEmployeeIds.includes(id))
    );
  };

  const handleConfirm = () => {
    if (!isNotNilOrEmpty(pickedEmployeeIds)) {
      return;
    }
    props.onConfirm(pickedEmployeeIds.map((employeeId) => ({employeeId})));
    closeCurrentDialog();
  };

  return (
    <VStack height="100%" spacing={4}>
      <Box flex={1} minHeight={0}>
        <DmsDataGrid
          gridCode={PARTICIPANTS_PICKER_GRID_CODE}
          onRowSelectionChange={handleSelectionChange}
          data-testid="recordAuditTeam-addDialog-dataGrid"
        />
      </Box>
      <ButtonGroup
        align="right"
        buttons={[
          {
            title: i18n.t('general.actions.cancel'),
            variant: 'secondary',
            onClick: closeCurrentDialog,
          },
          {
            title: i18n.t('general.actions.add'),
            onClick: handleConfirm,
            isDisabled: !isNotNilOrEmpty(pickedEmployeeIds),
            'data-testid': 'recordAuditTeam-addDialog-add',
          },
        ]}
      />
    </VStack>
  );
}
